import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LockClosedIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { authAPI } from '../services/api';

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token') || '';
    const [email, setEmail] = useState(searchParams.get('email') || '');
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError('');
        if (!token) {
            setError('This reset link is missing its token. Please request a new password reset email.');
            return;
        }
        if (password !== confirmation) {
            setError('Passwords do not match.');
            return;
        }

        setLoading(true);
        try {
            await authAPI.resetPassword({
                token,
                email,
                password,
                password_confirmation: confirmation,
            });
            navigate('/login?passwordReset=1');
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'This reset link is invalid or has expired. Please request a new one.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050509] flex items-center justify-center p-4 relative overflow-hidden">
            <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="w-full max-w-md bg-[#0B0B10] border border-white/10 p-8 rounded-2xl shadow-2xl relative z-10">
                <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8">
                    <ArrowLeftIcon className="w-4 h-4" /> Back to sign in
                </Link>
                <div className="mb-7">
                    <h1 className="text-2xl font-bold text-white mb-2">Choose a new password</h1>
                    <p className="text-slate-400 text-sm">Use at least 8 characters. The reset link expires after 60 minutes.</p>
                </div>
                {error && <div role="alert" className="mb-4 p-3 rounded-lg border border-red-500/20 bg-red-500/10 text-red-400 text-sm">{error}</div>}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-1">
                        <label htmlFor="email" className="text-xs font-medium text-slate-400 ml-1">Email address</label>
                        <input id="email" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} className="w-full bg-[#15151A] border border-white/10 rounded-lg py-2.5 px-4 text-slate-200 focus:outline-none focus:border-indigo-500/50" />
                    </div>
                    <div className="space-y-1">
                        <label htmlFor="password" className="text-xs font-medium text-slate-400 ml-1">New password</label>
                        <div className="relative">
                            <LockClosedIcon className="w-5 h-5 absolute left-3 top-2.5 text-slate-500" />
                            <input id="password" type="password" autoComplete="new-password" minLength={8} required value={password} onChange={(event) => setPassword(event.target.value)} className="w-full bg-[#15151A] border border-white/10 rounded-lg py-2.5 pl-10 pr-4 text-slate-200 focus:outline-none focus:border-indigo-500/50" />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <label htmlFor="confirmation" className="text-xs font-medium text-slate-400 ml-1">Confirm new password</label>
                        <div className="relative">
                            <LockClosedIcon className="w-5 h-5 absolute left-3 top-2.5 text-slate-500" />
                            <input id="confirmation" type="password" autoComplete="new-password" minLength={8} required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} className="w-full bg-[#15151A] border border-white/10 rounded-lg py-2.5 pl-10 pr-4 text-slate-200 focus:outline-none focus:border-indigo-500/50" />
                        </div>
                    </div>
                    <button type="submit" disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-2.5 rounded-lg transition-all disabled:opacity-70 disabled:cursor-not-allowed">
                        {loading ? 'Resetting password…' : 'Reset password'}
                    </button>
                </form>
            </div>
        </div>
    );
}
